import axios from "axios";
import { baseUrl } from "./util";

/**
 * Sync external datasets from OpenCirc for a product
 * Automatically creates a passport if one doesn't exist
 * @param {Object} params
 * @param {string} params.productId - Product ID
 * @param {string} params.containerId - OpenCirc passport/container ID (optional)
 * @param {string} params.platform - Platform for passport creation (optional, default: "bsdd")
 * @param {string} params.dictionary - Dictionary for passport creation (optional, default: "table6")
 * @param {string} params.dictionaryName - Name for passport (optional, default: "{product.name} - OpenCirc Passport")
 * @param {Function} params.onSuccess - Success callback (receives result with passport_id and passport_created)
 * @param {Function} params.onError - Error callback
 */
export const syncOpenCircDatasets = ({
  productId,
  containerId = null,
  platform = null,
  dictionary = null,
  dictionaryName = null,
  onSuccess = () => {},
  onError = () => {},
}) => {
  const requestBody = {};
  
  if (containerId) {
    requestBody.container_id = containerId;
  }
  if (platform) {
    requestBody.platform = platform;
  }
  if (dictionary) {
    requestBody.dictionary = dictionary;
  }
  if (dictionaryName) {
    requestBody.dictionary_name = dictionaryName;
  }

  const config = {
    method: "post",
    url: `${baseUrl}/api/business/opencirc/product/${productId}/sync-datasets`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      "Content-Type": "application/json",
    },
    data: JSON.stringify(requestBody),
  };

  axios(config)
    .then(function (response) {
      if (response.data.message === "ok") {
        onSuccess(response.data);
      } else {
        onError(response.data.error || "Unknown error");
      }
    })
    .catch(function (error) {
      console.error("Sync OpenCirc datasets error:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to sync datasets";
      onError(errorMessage);
    });
};

/**
 * Activate external dataset for a component
 * @param {Object} params
 * @param {string} params.productId - Product ID
 * @param {string} params.componentId - Component ID
 * @param {string} params.datasetId - Dataset ID (optional)
 * @param {Function} params.onSuccess - Success callback
 * @param {Function} params.onError - Error callback
 */
export const activateExternalDataset = ({
  productId,
  componentId,
  datasetId = null,
  onSuccess = () => {},
  onError = () => {},
}) => {
  const config = {
    method: "post",
    url: `${baseUrl}/api/business/opencirc/product/${productId}/component/${componentId}/activate-dataset`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      "Content-Type": "application/json",
    },
    data: JSON.stringify({
      dataset_id: datasetId,
    }),
  };

  axios(config)
    .then(function (response) {
      if (response.data.message === "ok") {
        onSuccess(response.data.data);
      } else {
        onError(response.data.error || "Unknown error");
      }
    })
    .catch(function (error) {
      console.error("Activate dataset error:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to activate dataset";
      onError(errorMessage);
    });
};

/**
 * Reject external dataset for a component
 * @param {Object} params
 * @param {string} params.productId - Product ID
 * @param {string} params.componentId - Component ID
 * @param {string} params.datasetId - Dataset ID (optional)
 * @param {Function} params.onSuccess - Success callback
 * @param {Function} params.onError - Error callback
 */
export const rejectExternalDataset = ({
  productId,
  componentId,
  datasetId = null,
  onSuccess = () => {},
  onError = () => {},
}) => {
  const config = {
    method: "post",
    url: `${baseUrl}/api/business/opencirc/product/${productId}/component/${componentId}/reject-dataset`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      "Content-Type": "application/json",
    },
    data: JSON.stringify({
      dataset_id: datasetId,
    }),
  };

  axios(config)
    .then(function (response) {
      if (response.data.message === "ok") {
        onSuccess(response.data.data);
      } else {
        onError(response.data.error || "Unknown error");
      }
    })
    .catch(function (error) {
      console.error("Reject dataset error:", error);
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to reject dataset";
      onError(errorMessage);
    });
};

/**
 * Fetch bSDD dictionary classes dropdown from OpenCirc
 * Uses OpenCirc's treeStructure API internally
 * @param {Object} params
 * @param {string} params.dictionary - Dictionary type: "table6" (BR18 Table 6) or "ifc" (optional, default: "table6")
 * @param {string} params.search - Search term for filtering (optional)
 * @param {Function} params.setDropdown - Callback to set dropdown data
 */
export const getBSDDDictionaryClassesDropdown = ({
  dictionary = "table6",
  search = "",
  setDropdown = () => {},
}) => {
  const params = new URLSearchParams({
    dictionary: dictionary,
  });
  
  if (search) {
    params.append("search", search);
  }
  
  const config = {
    method: "get",
    url: `${baseUrl}/api/business/bsdd-dictionary-classes-dropdown?${params.toString()}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("access_token")}`,
      "Content-Type": "application/json",
    },
  };

  axios(config)
    .then(function (response) {
      if (response.data.message === "ok" && response.data.data) {
        // Format data similar to prod-component-dropdown format
        const formattedData = response.data.data.map((item) => {
          if (item.isCategory) {
            return {
              isCategory: true,
              name: item.name || item.value || item.category,
              value: item.value || item.name || item.category,
              category: item.category || item.name || item.value,
            };
          } else {
            return {
              id: item.id,
              name: item.name,
              value: item.code || item.value || item.name, // Use code as value
              code: item.code || item.value,
              uri: item.uri,
              category: item.category,
              characterization_factors: item.characterization_factors,
            };
          }
        });
        setDropdown(formattedData);
      } else {
        console.error("Invalid response format:", response.data);
        setDropdown([]);
      }
    })
    .catch(function (error) {
      console.error("Failed to fetch bSDD classes:", error);
      setDropdown([]);
    });
};


